import * as React from 'react'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { default as Button } from '@mulesoft/anypoint-components/lib/Button'
import { EnvironmentSelectorProps } from '../../environment/model'
import { ResourceSelectorProps } from '../../resource/model'
import { VersionSelectorProps } from '../../version/model'
import MonitoringSelector from '@mulesoft/monitoring-center-components/lib/components/Selector/MonitoringSelector/MonitoringSelector'
import './MonitoringLandingPage.css'

export type ValueProps = {
  title: string
  subTitle: string
  docsLink?: string
  footer?: React.ReactNode
  image?: React.ReactNode
  environmentSelector: EnvironmentSelectorProps
  resourceSelector: ResourceSelectorProps
  versionSelector: VersionSelectorProps
  testIds?: {
    title?: string
    subTitle?: string
    docsLink?: string
    footer?: string
    image?: string
    environmentSelector?: string,
    resourceSelector?: string,
    versionSelector?: string,
    viewButton?: string
  }
}

export type EventProps = {
  componentWillMount?: () => void
  environmentSelected: (environmentId: string) => void
  resourceSelected: (resourceId: string, resourceType: ResourceType) => void
  versionSelected: (versionId: string) => void
  onView: (envId: string, resourceId: string, versionId?: string) => void
}

export type Props = ValueProps & EventProps

export type State = {
  envId?: string,
  resourceId?: string,
  versionId?: string
}

class MonitoringLandingPage extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props)

    this.getMonitoringSelectorProps = this.getMonitoringSelectorProps.bind(this)
    this.getViewButtonProps = this.getViewButtonProps.bind(this)
    this.environmentSelected = this.environmentSelected.bind(this)
    this.resourceSelected = this.resourceSelected.bind(this)
    this.versionSelected = this.versionSelected.bind(this)
    this.onViewClick = this.onViewClick.bind(this)

    this.state = {
      envId: props.environmentSelector.selected ? props.environmentSelector.selected.id : undefined,
      resourceId: props.resourceSelector.selected ? props.resourceSelector.selected.id : undefined,
      versionId: props.versionSelector.selected ? props.versionSelector.selected.id.toString() : undefined
    }
  }

  environmentSelected(environmentId: string) {
    this.props.environmentSelected(environmentId)
    this.setState({...this.state, envId: environmentId, resourceId: undefined, versionId: undefined})
  }

  resourceSelected(resourceId: string, resourceType: ResourceType) {
    this.props.resourceSelected(resourceId, resourceType)
    this.setState({...this.state, resourceId: resourceId, versionId: undefined})
  }

  versionSelected(versionId: string) {
    this.props.versionSelected(versionId)
    this.setState({...this.state, versionId: versionId})
  }

  onViewClick() {
    const {envId, resourceId, versionId} = this.state
    if (envId && resourceId) this.props.onView(envId, resourceId, versionId)
  }

  getViewButtonProps() {
    const testIds = this.props.testIds
    const environmentSelector = this.props.environmentSelector
    const resourceSelector = this.props.resourceSelector
    const versionSelector = this.props.versionSelector
    const environmentSelected = environmentSelector.selected !== undefined
    const resourceSelected = resourceSelector.selected !== undefined && this.state.resourceId !== undefined
    const isResourceAPI = resourceSelector.selected && resourceSelector.selected.resourceType === ResourceType.API
    const versionSelected = isResourceAPI ? versionSelector.selected !== undefined : true
    const isEnabled = environmentSelected && resourceSelected && versionSelected

    return {
      className: 'monitoring-landing-page-view-button',
      kind: 'primary' as 'primary',
      testId: testIds && testIds.viewButton ? testIds.viewButton : 'view-resource-btn',
      disabled: !isEnabled,
      onClick: this.onViewClick
    }
  }

  getMonitoringSelectorProps() {
    return {
      environmentSelector: this.props.environmentSelector,
      resourceSelector: this.props.resourceSelector,
      versionSelector: this.props.versionSelector,
      environmentSelected: this.environmentSelected,
      resourceSelected: this.resourceSelected,
      versionSelected: this.versionSelected
    }
  }

  componentWillMount(): void {
    if (this.props.componentWillMount) this.props.componentWillMount()
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, prevContext: any): void {
    const {environmentSelector, resourceSelector, versionSelector} = this.props
    if (prevProps.environmentSelector.selected !== environmentSelector.selected && environmentSelector.selected) {
      this.setState({...this.state, envId: environmentSelector.selected.id})
    }
    if (prevProps.resourceSelector.selected !== resourceSelector.selected && resourceSelector.selected) {
      this.setState({...this.state, resourceId: resourceSelector.selected.id})
    }
    if (prevProps.versionSelector.selected !== versionSelector.selected && versionSelector.selected) {
      this.setState({...this.state, versionId: versionSelector.selected.id.toString()})
    }
  }

  render() {
    const monitoringSelectorProps = this.getMonitoringSelectorProps()
    const viewButtonProps = this.getViewButtonProps()
    const titleTestId = this.props.testIds && this.props.testIds.title
    const subTitleTestId = this.props.testIds && this.props.testIds.subTitle
    const docsLinkTestId = this.props.testIds && this.props.testIds.docsLink
    const footerTestId = this.props.testIds && this.props.testIds.docsLink
    const imageTestId = this.props.testIds && this.props.testIds.docsLink

    return (
      <div className={'monitoring-landing-page-container'}>
        <div className={'monitoring-landing-page-header'}>
          <div className={'monitoring-landing-page-title'} data-test-id={titleTestId}>
            <span>{this.props.title}</span>
          </div>
          <div className={'monitoring-landing-page-subtitle'} data-test-id={subTitleTestId}>
            <span>{this.props.subTitle}</span>
          </div>
          {this.props.docsLink &&
          <span className={'monitoring-landing-page-subtitle'} data-testid={docsLinkTestId}>
            <a href={this.props.docsLink} target={'_blank'}>Visit the documentation</a> to learn more.
          </span>
          }
        </div>
        <div className={'monitoring-landing-page-selector-container'}>
          <div className="monitoring-landing-page-selector">
            <MonitoringSelector {...monitoringSelectorProps} />
            <div className={'monitoring-landing-page-view-button'}>
              <Button {...viewButtonProps}>View</Button>
            </div>
          </div>
          <div className="monitoring-landing-page-footer-text" data-test-id={footerTestId}>
            {this.props.footer}
          </div>
        </div>
        <div className={'monitoring-landing-page-image'} data-test-id={imageTestId}>
          {this.props.image}
        </div>
      </div>
    )
  }
}

export default MonitoringLandingPage
